import { memo } from 'react';
import { View, Text, ScrollView, TouchableOpacity } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Unicon from './Unicon';
import Callout from './Callout';

const RecipientList = ({ recipients, mode = 'email', onRemove, deduplicateContacts = false }) => {
    const { theme: { colors, fonts } } = useUnistyles();

    const rows = recipients.map((person) => {
        let contactValue = null;
        let isValid = false;

        if (mode === 'email') {
            contactValue = person.email || null;
            isValid = !!contactValue;
        } else {
            const smsPhone = person.Phones?.find(p => p.smsOptInStatus === 'opted_in');
            contactValue = smsPhone?.phoneNumber?.trim() || null;
            isValid = !!contactValue;
        }

        return {
            id: person.id,
            name: person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unnamed',
            contactValue,
            isValid,
        };
    });

    // Detect duplicate contact values. SMS numbers are compared after removing
    // formatting so variants of the same number dedupe consistently.
    const seenContacts = new Set();
    const duplicateRowIds = new Set();

    rows.forEach((row) => {
        if (!row.contactValue) return;
        const contactKey = mode === 'sms' ? row.contactValue.replace(/[^\d+]/g, '') : row.contactValue;
        if (seenContacts.has(contactKey)) duplicateRowIds.add(row.id);
        else seenContacts.add(contactKey);
    });

    const displayRows = rows.map(row => deduplicateContacts && duplicateRowIds.has(row.id)
        ? { ...row, isValid: false, skippedReason: 'Duplicate number' }
        : row);
    const validRows = displayRows.filter(r => r.isValid);
    const skippedRows = displayRows.filter(r => !r.isValid);

    const hasDuplicates = duplicateRowIds.size > 0;

    const renderRow = (row) => {
        const isDuplicate = duplicateRowIds.has(row.id);
        return (
            <View key={row.id} style={[styles.row, !row.isValid && styles.rowMuted]}>
                <Text style={[styles.name, { fontFamily: fonts.heading, color: row.isValid ? colors.text : colors.textTertiary }]}>
                    {row.name}
                </Text>
                <View style={styles.rowRight}>
                    {row.isValid ? (
                        <>
                            {isDuplicate && (
                                <View style={styles.duplicateBadge}>
                                    <Text style={[styles.duplicateText, { fontFamily: fonts.body, color: colors.textTertiary }]}>
                                        duplicate
                                    </Text>
                                </View>
                            )}
                            <Text style={[styles.contact, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                                {row.contactValue}
                            </Text>
                        </>
                    ) : (
                        <View style={styles.missingBadge}>
                            <Unicon name="ban" size={12} colorVariant="textTertiary" />
                            <Text style={[styles.missingText, { fontFamily: fonts.body, color: colors.textTertiary }]}>
                                {row.skippedReason || (mode === 'email' ? 'No email' : 'No SMS enabled number')}
                            </Text>
                        </View>
                    )}
                    {onRemove && (
                        <TouchableOpacity onPress={() => onRemove(row.id)} style={styles.removeButton}>
                            <Unicon name="close" size={14} colorVariant="error" />
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        );
    };

    return (
        <View style={styles.container}>
            {hasDuplicates && (
                <Callout
                    type="warning"
                    message={`Duplicate ${mode === 'email' ? 'emails' : 'numbers'} detected. Variables will use the first person's details.`}
                />
            )}

            <View style={styles.summaryRow}>
                <Text style={[styles.summaryText, { fontFamily: fonts.body, color: colors.textSecondary }]}>
                    {validRows.length} {validRows.length === 1 ? 'person' : 'people'} will receive this message
                </Text>
                {skippedRows.length > 0 && (
                    <View style={styles.warningBadge}>
                        <Unicon name="warning" size={12} colorVariant="warning" />
                        <Text style={[styles.warningText, { fontFamily: fonts.body, color: colors.warning }]}>
                            {skippedRows.length} skipped
                        </Text>
                    </View>
                )}
            </View>

            <ScrollView style={styles.scrollArea} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                {validRows.map(renderRow)}
                {skippedRows.length > 0 && (
                    <View style={styles.skippedSection}>
                        <Text style={[styles.skippedHeader, { fontFamily: fonts.heading, color: colors.textSecondary }]}>
                            Skipped
                        </Text>
                        {skippedRows.map(renderRow)}
                    </View>
                )}
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    container: {
        marginBottom: 16,
    },
    summaryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    summaryText: {
        fontSize: 13,
    },
    warningBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: colors.warningContainer,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4,
    },
    warningText: {
        fontSize: 11,
    },
    scrollArea: {
        maxHeight: 200,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 8,
        backgroundColor: colors.surface,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        gap: 8,
    },
    rowMuted: {
        backgroundColor: colors.background,
    },
    name: {
        fontSize: 13,
        flex: 1,
    },
    contact: {
        fontSize: 12,
    },
    missingBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    missingText: {
        fontSize: 12,
    },
    skippedSection: {
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: 8,
        marginTop: 4,
    },
    skippedHeader: {
        fontSize: 12,
        paddingHorizontal: 12,
        paddingVertical: 6,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    rowRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    duplicateBadge: {
        backgroundColor: colors.background,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: colors.border,
    },
    duplicateText: {
        fontSize: 10,
    },
    removeButton: {
        padding: 4,
        marginLeft: 4,
    },
}));

export default memo(RecipientList);
